import React, { useState } from 'react';

import { Modal } from '~/eds/src/components/Modal/Modal';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Modal_DEPRECATED from '~/ui/components/Modal/Modal';
import SortableList from '~/ui/components/SortableList/SortableList';

const promise = () => new Promise((resolve) => resolve(true));

// Map old Modal.jsx variants to new EDS Button variants for backwards compatibility
function mapVariant(oldVariant) {
  if (!oldVariant) {
    return 'primary';
  }
  const variantMap = {
    red: 'danger',
    link: 'action',
    ghost: 'tertiary',
    outlined: 'secondary',
    //EDS modals don't support green variant, so we're using primary as a fallback
    green: 'primary',
  };

  return variantMap[oldVariant] || oldVariant;
}

export function useModal({
  content,
  disableAutoHide,
  disableCancelButton,
  disableCloseButton,
  overflowDisplay,
  title,
  width,
  actionButton,
  secondaryButton,
  onHide,
  onCancel,
}) {
  const enableModalConsolidation = useFlag(FlagType.ModalConsolidation);
  const [visible, setVisible] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isSecondaryActionLoading, setIsSecondaryActionLoading] =
    useState(false);

  function showModal() {
    setVisible(true);
  }

  function hideModal() {
    onHide && onHide();
    setVisible(false);
  }

  function cancelModal() {
    onCancel && onCancel();
    setVisible(false);
  }

  const submitAction = async () => {
    setIsActionLoading(true);
    try {
      await actionButton?.promise();
      hideModal();
    } catch (error) {
      if (actionButton?.errorHandler) {
        actionButton?.errorHandler(error);
      }
    } finally {
      setIsActionLoading(false);
    }
  };

  const submitSecondaryAction = async () => {
    setIsSecondaryActionLoading(true);
    try {
      await secondaryButton?.promise();
      hideModal();
    } catch (error) {
      if (secondaryButton?.errorHandler) {
        secondaryButton?.errorHandler(error);
      }
    } finally {
      setIsSecondaryActionLoading(false);
    }
  };

  const modal = enableModalConsolidation ? (
    <Modal
      primaryAction={{
        text: actionButton?.text,
        variant: mapVariant(actionButton?.variant),
        disabled: actionButton?.disabled,
        tooltip: actionButton?.tooltip,
        isLoading: isActionLoading || actionButton?.isLoading,
        onClick: submitAction,
      }}
      disableHideOnEscape={disableAutoHide}
      overflow={overflowDisplay}
      secondaryActions={
        secondaryButton
          ? [
              {
                text: secondaryButton?.text,
                variant: mapVariant(secondaryButton?.variant),
                disabled: secondaryButton?.disabled,
                tooltip: secondaryButton?.tooltip,
                isLoading:
                  isSecondaryActionLoading || secondaryButton?.isLoading,
                onClick: submitSecondaryAction,
              },
            ]
          : []
      }
      title={title}
      isVisible={visible}
      width={width}
      onHide={hideModal}
      onCancel={disableCloseButton ? null : cancelModal}
      cancelText={disableCancelButton ? null : undefined}
    >
      {content}
    </Modal>
  ) : (
    <Modal_DEPRECATED
      actionButton={actionButton}
      disableAutoHide={disableAutoHide}
      disableCancelButton={disableCancelButton}
      disableCloseButton={disableCloseButton}
      overflowDisplay={overflowDisplay}
      secondaryButton={secondaryButton}
      title={title}
      visible={visible}
      width={width}
      onHide={hideModal}
      onCancel={cancelModal}
    >
      {content}
    </Modal_DEPRECATED>
  );

  return [modal, showModal];
}

export function useModalSimple({
  confirm,
  content,
  disableAutoHide,
  disableCancelButton,
  text,
  title,
  width,
  onHide,
}) {
  const [modal, showModal] = useModal({
    content,
    disableAutoHide,
    disableCancelButton,
    title,
    actionButton: {
      text,
      errorHandler: () => {},
      promise: async () => {
        await promise();
        confirm();
      },
    },
    width,
    onHide,
  });

  return [modal, showModal];
}

export function useModalSortable({
  confirm,
  contentText,
  items: defaultItems,
  text,
  title,
}) {
  const [items, setItems] = useState(defaultItems);

  const [modal, showModal] = useModalSimple({
    confirm: () => confirm(items),
    disableCancelButton: true,
    text,
    title,
    content: (
      <FlexLayout flexDirection="column" space={4}>
        {contentText}
        <SortableList items={defaultItems} onSort={setItems} />
      </FlexLayout>
    ),
  });

  return [modal, showModal];
}
